import { useEffect, useRef } from "react";
import {
    $createParagraphNode,
    $getRoot,
    $isElementNode,
    $isDecoratorNode,
    type ElementNode,
} from "lexical";
import { $createHeadingNode, $isHeadingNode, type HeadingTagType } from "@lexical/rich-text";
import { $generateHtmlFromNodes, $generateNodesFromDOM } from "@lexical/html";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";

export type EmptyBlockType = "paragraph" | HeadingTagType;

interface HtmlSyncProps {
    value: string;
    onChange: (html: string) => void;
    emptyBlockType?: EmptyBlockType;
}

function createEmptyBlock(type: EmptyBlockType): ElementNode {
    if (type === "paragraph") return $createParagraphNode();
    return $createHeadingNode(type);
}

export function HtmlSync({ value, onChange, emptyBlockType = "paragraph" }: HtmlSyncProps) {
    const [editor] = useLexicalComposerContext();
    const lastSyncedRef = useRef<string | null>(null);
    const onChangeRef = useRef(onChange);
    onChangeRef.current = onChange;

    useEffect(() => {
        if (value === lastSyncedRef.current) return;

        editor.update(() => {
            const root = $getRoot();
            const parser = new DOMParser();
            const dom = parser.parseFromString(value || "", "text/html");
            const nodes = $generateNodesFromDOM(editor, dom);

            root.clear();

            const allEmpty =
                nodes.length === 0 ||
                nodes.every(
                    (n) =>
                        $isElementNode(n) &&
                        (n as ElementNode).getChildrenSize() === 0 &&
                        (n as ElementNode).getTextContentSize() === 0,
                );

            if (allEmpty) {
                const first = nodes[0];
                if (emptyBlockType !== "paragraph" && first && $isHeadingNode(first)) {
                    root.append(first);
                } else {
                    root.append(createEmptyBlock(emptyBlockType));
                }
            } else {
                for (const node of nodes) {
                    if ($isElementNode(node) || $isDecoratorNode(node)) {
                        root.append(node);
                    } else {
                        const paragraph = $createParagraphNode();
                        paragraph.append(node);
                        root.append(paragraph);
                    }
                }
            }

            const firstChild = root.getFirstChild();
            if (firstChild && $isElementNode(firstChild)) {
                firstChild.selectStart();
            }

            lastSyncedRef.current = value;
        });
    }, [value, editor, emptyBlockType]);

    useEffect(() => {
        return editor.registerUpdateListener(({ editorState }) => {
            editorState.read(() => {
                const html = $generateHtmlFromNodes(editor);
                if (html === lastSyncedRef.current) return;
                lastSyncedRef.current = html;
                onChangeRef.current(html);
            });
        });
    }, [editor]);

    return null;
}
